/** Allowed appreciation reactions for closed tasks (WhatsApp-style). */
export const TASK_APPRECIATION_EMOJIS = ['👍', '👏', '🎉', '🚀'] as const;

export type TaskAppreciationEmoji = (typeof TASK_APPRECIATION_EMOJIS)[number];

/** Strip emoji presentation selectors so stored/rendered forms still match. */
export function normalizeAppreciationEmoji(emoji: string): string {
  return emoji.trim().replace(/\uFE0F/g, '');
}

export function isAllowedTaskAppreciationEmoji(
  emoji: string | null | undefined,
): emoji is TaskAppreciationEmoji {
  const e = normalizeAppreciationEmoji(emoji ?? '');
  return (TASK_APPRECIATION_EMOJIS as readonly string[]).some(
    (allowed) => normalizeAppreciationEmoji(allowed) === e,
  );
}

export function canonicalizeAppreciationEmoji(
  emoji: string | null | undefined,
): TaskAppreciationEmoji | undefined {
  const normalized = normalizeAppreciationEmoji(emoji ?? '');
  if (!normalized) return undefined;
  return (TASK_APPRECIATION_EMOJIS as readonly string[]).find(
    (allowed) => normalizeAppreciationEmoji(allowed) === normalized,
  ) as TaskAppreciationEmoji | undefined;
}

/** uid → emoji. Supports `appreciations` map and legacy single `appreciation`. */
export function parseTaskAppreciations(
  data: Record<string, unknown> | null | undefined,
): Record<string, TaskAppreciationEmoji> {
  if (!data) return {};
  const out: Record<string, TaskAppreciationEmoji> = {};

  const multi = data.appreciations;
  if (multi && typeof multi === 'object') {
    for (const [uidRaw, value] of Object.entries(multi as Record<string, unknown>)) {
      const uid = uidRaw.trim();
      if (!uid) continue;
      let candidate = '';
      if (value && typeof value === 'object' && 'emoji' in value) {
        candidate = String((value as { emoji?: unknown }).emoji ?? '');
      } else if (typeof value === 'string') {
        candidate = value;
      }
      const emoji = canonicalizeAppreciationEmoji(candidate);
      if (emoji) out[uid] = emoji;
    }
  }

  if (Object.keys(out).length === 0) {
    const legacy = data.appreciation;
    if (legacy && typeof legacy === 'object') {
      const uid = String((legacy as { by?: unknown }).by ?? '').trim();
      const emoji = canonicalizeAppreciationEmoji(
        String((legacy as { emoji?: unknown }).emoji ?? ''),
      );
      if (uid && emoji) out[uid] = emoji;
    }
  }

  return out;
}

/** Distinct emojis with counts, highest count first. */
export function appreciationEmojiCounts(
  byUid: Record<string, string>,
): { emoji: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const emoji of Object.values(byUid)) {
    counts.set(emoji, (counts.get(emoji) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([emoji, count]) => ({ emoji, count }));
}

/** @deprecated Prefer [parseTaskAppreciations]. */
export function parseTaskAppreciationEmoji(raw: unknown): TaskAppreciationEmoji | undefined {
  if (raw && typeof raw === 'object' && 'emoji' in raw) {
    return canonicalizeAppreciationEmoji(String((raw as { emoji?: unknown }).emoji ?? ''));
  }
  if (typeof raw === 'string') return canonicalizeAppreciationEmoji(raw);
  return undefined;
}
